import { useEffect, useState } from "react";
import { ExternalLink, Leaf, Waves } from "lucide-react";
import { fetchProject } from "@/lib/api";
import { ABOUT } from "@/constants/testIds";
import EtwinningBadge from "@/components/EtwinningBadge";
import CountryFlag from "@/components/CountryFlag";
import { useT } from "@/lib/i18n";

export default function AboutPage() {
    const { t } = useT();
    const [project, setProject] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const data = await fetchProject();
                if (!cancelled) setProject(data);
            } catch (e) {
                /* noop */
            }
        };
        load();
        window.scrollTo({ top: 0, behavior: "instant" });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div data-testid={ABOUT.page}>
            <section className="container-pad py-20 md:py-32">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-3">
                        <div className="caption text-brand-green">{t("about.eyebrow")}</div>
                        <div className="mt-6 flex flex-col gap-5">
                            <img
                                src="/images/stemcycle-g2b-logo.jpg"
                                alt="STEMcycle G2B official logo"
                                data-testid="about-stemcycle-logo"
                                className="w-40 h-40 object-cover rounded-full ring-1 ring-black/5"
                            />
                            <EtwinningBadge href={project?.etwinning_url} />
                            <div className="flex flex-col gap-2 mt-2 text-[13px] leading-snug">
                                <div className="flex items-center gap-2">
                                    <CountryFlag country="Türkiye" size="lg" />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-brand-gold">
                                            Co-founder
                                        </div>
                                        <div className="text-ink-primary font-medium">
                                            Istanbul TOKİ Kayaşehir MTAL
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CountryFlag country="Romania" size="lg" />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-brand-gold">
                                            Co-founder
                                        </div>
                                        <div className="text-ink-primary font-medium">
                                            Liceul Teoretic „Aurel Vlaicu&quot;, Breaza
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center gap-3 text-ink-muted">
                            <Leaf size={18} strokeWidth={1.5} className="text-brand-green" />
                            <span className="h-[1px] flex-1 bg-gradient-to-r from-brand-green to-brand-blue" />
                            <Waves size={18} strokeWidth={1.5} className="text-brand-blue" />
                        </div>
                    </div>
                    <div className="md:col-span-9">
                        <h1 className="heading-serif text-5xl md:text-6xl text-ink-primary leading-[1] tracking-tight text-balance">
                            {t("about.heading1")}
                            <br />
                            <em className="gradient-g2b-text">{t("about.heading2")}</em>
                        </h1>
                        <p className="mt-8 text-xl md:text-2xl text-ink-secondary leading-relaxed max-w-3xl">
                            {project?.project_intro}
                        </p>

                        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Block title={t("about.workline")}>{t("about.worklineBody")}</Block>
                            <Block title={t("about.partners")}>{t("about.partnersBody")}</Block>
                            <Block title={t("about.method")}>{t("about.methodBody")}</Block>
                            <Block title={t("about.goal")}>{t("about.goalBody")}</Block>
                        </div>

                        {project?.etwinning_url && (
                            <a
                                href={project.etwinning_url}
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid={ABOUT.etwinningLink}
                                className="btn-primary mt-12"
                            >
                                {t("common.visit")}
                                <ExternalLink size={14} strokeWidth={1.6} />
                            </a>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Block({ title, children }) {
    return (
        <div className="card-flat p-6">
            <div className="caption text-brand-blue mb-2">{title}</div>
            <p className="text-ink-secondary leading-relaxed text-[16px]">{children}</p>
        </div>
    );
}
